// src/errors/utils/error-logger.ts

/**
 * Error Logger Utility
 *
 * WHY: Structured, consistent logging across the application
 * WHAT: Logger with levels, formatting, and environment awareness
 * BENEFIT:
 *   - Consistent log format for debugging
 *   - Environment-specific behavior (verbose in dev, minimal in prod)
 *   - Structured data for log aggregation services
 *
 * DESIGN DECISIONS:
 * 1. Uses console in development for instant feedback
 * 2. Can be extended to send to external services in production
 * 3. Respects log level configuration
 * 4. Groups related log entries for readability
 */

import { errorConfig, shouldLog } from "@/config";
import { type SerializedError, ErrorSeverity } from "../types";
import { logger } from "@/core/utils";

/**
 * Log levels in order of verbosity
 */
type LogLevel = "debug" | "info" | "warn" | "error";

/**
 * Structured log entry
 */
interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  data?: unknown;
  error?: SerializedError;
}

/**
 * Console styles for different log levels
 */
const LOG_STYLES: Record<LogLevel, string> = {
  debug: "color: #6B7280; font-weight: normal;",
  info: "color: #3B82F6; font-weight: normal;",
  warn: "color: #F59E0B; font-weight: bold;",
  error: "color: #EF4444; font-weight: bold;",
};

/**
 * Emoji prefixes for log levels
 */
const LOG_EMOJI: Record<LogLevel, string> = {
  debug: "🔍",
  info: "ℹ️",
  warn: "⚠️",
  error: "❌",
};

/**
 * Error Logger Class
 *
 * Provides structured logging with environment awareness.
 */
class ErrorLogger {
  /**
   * Format timestamp for logs
   */
  private formatTimestamp(): string {
    return new Date().toISOString();
  }

  /**
   * Create log entry object
   */
  private createEntry(
    level: LogLevel,
    message: string,
    data?: unknown,
    error?: SerializedError,
  ): LogEntry {
    return {
      level,
      message,
      timestamp: this.formatTimestamp(),
      data,
      error,
    };
  }

  /**
   * Output log to console (development)
   */
  private outputToConsole(entry: LogEntry): void {
    const { level, message, timestamp, data, error } = entry;
    const style = LOG_STYLES[level];
    const emoji = LOG_EMOJI[level];

    // Use console groups for better organization
    if (errorConfig.logging.prettyPrint) {
      console.groupCollapsed(
        `%c${emoji} [${level.toUpperCase()}] ${message}`,
        style,
      );
      console.log("Timestamp:", timestamp);

      if (data) {
        console.log("Data:", data);
      }

      if (error) {
        console.log("Error Details:", error);
        if (error.stack && errorConfig.logging.includeStackTrace) {
          console.log("Stack Trace:", error.stack);
        }
      }

      console.groupEnd();
    } else {
      // Simple format for non-pretty print
      const logFn = console[level] || console.log;
      logFn(`[${timestamp}] [${level.toUpperCase()}] ${message}`, data || "");
    }
  }

  /**
   * Send log to external service (production)
   * Override this method to integrate with your logging service
   */
  private sendToExternalService(entry: LogEntry): void {
    // TODO: Integrate with your logging service
    // Examples: Datadog, LogRocket, CloudWatch, etc.
    //
    // Example with a hypothetical logging service:
    logger.info("entry in sendToExternalService", entry);
    // loggingService.send({
    //   level: entry.level,
    //   message: entry.message,
    //   timestamp: entry.timestamp,
    //   metadata: entry.data,
    //   error: entry.error,
    // });
  }

  /**
   * Process and output log entry
   */
  private log(
    level: LogLevel,
    message: string,
    data?: unknown,
    error?: SerializedError,
  ): void {
    // Check if we should log at this level
    if (!shouldLog(level)) {
      return;
    }

    const entry = this.createEntry(level, message, data, error);

    // Output based on environment
    if (errorConfig.logging.enabled) {
      this.outputToConsole(entry);
    }

    // In production, also send to external service
    if (
      errorConfig.reporting.enabled &&
      (level === "error" || level === "warn")
    ) {
      this.sendToExternalService(entry);
    }
  }

  /**
   * Debug level log
   * For detailed debugging information
   */
  debug(message: string, data?: unknown): void {
    this.log("debug", message, data);
  }

  /**
   * Info level log
   * For general informational messages
   */
  info(message: string, data?: unknown): void {
    this.log("info", message, data);
  }

  /**
   * Warning level log
   * For potentially harmful situations
   */
  warn(message: string, data?: unknown): void {
    this.log("warn", message, data);
  }

  /**
   * Error level log
   * For error events
   */
  error(message: string, data?: unknown): void {
    this.log("error", message, data);
  }

  /**
   * Log a serialized error
   * Automatically determines log level from severity
   */
  logError(error: SerializedError): void {
    const level = this.severityToLogLevel(error.severity);
    this.log(level, error.message, undefined, error);
  }

  /**
   * Log API request/response for debugging
   */
  logApiCall(
    method: string,
    url: string,
    duration: number,
    status?: number,
    error?: SerializedError,
  ): void {
    const data = { method, url, duration: `${duration}ms`, status };

    if (error) {
      this.log("error", `API Error: ${method} ${url}`, data, error);
    } else if (duration > 3000) {
      this.log("warn", `Slow API Call: ${method} ${url}`, data);
    } else {
      this.log("debug", `API Call: ${method} ${url}`, data);
    }
  }

  /**
   * Convert error severity to log level
   */
  private severityToLogLevel(severity: ErrorSeverity): LogLevel {
    switch (severity) {
      case ErrorSeverity.LOW:
        return "info";
      case ErrorSeverity.MEDIUM:
        return "warn";
      case ErrorSeverity.HIGH:
      case ErrorSeverity.CRITICAL:
        return "error";
      default:
        return "error";
    }
  }
}

/**
 * Singleton logger instance
 */
export const errorLogger = new ErrorLogger();
